import { Cable } from "../model/viewModel/cable";

export const getFeederColourKey = (
  substationId: string | null | undefined,
  feederNumber: number | string | null | undefined,
): string => `${substationId ?? ""}-${feederNumber ?? ""}`;

const hasFeeder = (cable: Cable): boolean =>
  cable.feederNumber !== null && cable.feederNumber !== undefined;

const buildFeederColourMap = (
  cables: Cable[],
  feederColors: Record<string, string>,
): Map<string, string> => {
  const colours = new Map<string, string>();
  const queue: Cable[] = [];

  cables.forEach((cable) => {
    const colour = feederColors[getFeederColourKey(cable.substationId, cable.feederNumber)];
    if (hasFeeder(cable) && colour) {
      colours.set(cable.id, colour);
      queue.push(cable);
    }
  });

  if (queue.length === 0) return colours;

  // cables added after import have no feeder number, so inherit the colour of what they connect to
  const inheritors = cables.filter((cable) => !hasFeeder(cable));
  if (inheritors.length === 0) return colours;

  const byEndpoint = new Map<string, Cable[]>();
  inheritors.forEach((cable) => {
    [cable.startAssetId, cable.endAssetId].forEach((endpoint) => {
      if (!endpoint) return;
      const existing = byEndpoint.get(endpoint);
      if (existing) existing.push(cable);
      else byEndpoint.set(endpoint, [cable]);
    });
  });

  while (queue.length > 0) {
    const cable = queue.shift() as Cable;
    const colour = colours.get(cable.id) as string;

    [cable.startAssetId, cable.endAssetId].forEach((endpoint) => {
      (byEndpoint.get(endpoint) ?? []).forEach((connected) => {
        if (colours.has(connected.id)) return;
        colours.set(connected.id, colour);
        queue.push(connected);
      });
    });
  }

  return colours;
};

let cachedCables: Cable[] | null = null;
let cachedFeederColors: Record<string, string> | null = null;
let cachedMap: Map<string, string> = new Map();

export const getFeederColourMap = (
  cables: Cable[],
  feederColors: Record<string, string> | undefined,
): Map<string, string> => {
  const colors = feederColors ?? {};
  if (cachedCables === cables && cachedFeederColors === colors) return cachedMap;

  cachedMap = buildFeederColourMap(cables ?? [], colors);
  cachedCables = cables;
  cachedFeederColors = colors;
  return cachedMap;
};
